import { FeatureLayer } from "./WmsLayerFunc";

export const HISTORICAL_ORTHOPHOTO_GROUP = "Historiske Ortofoto";
export const HISTORICAL_ORTHOPHOTO_CONTROL_LABEL = "geodanmark 2015 - nuværende 12 5cm";
export const HISTORICAL_ORTHOPHOTO_CURRENT_ID = "current";
export const HISTORICAL_ORTHOPHOTO_CURRENT_LABEL = "Nuværende";

export type HistoricalOrthophotoOption = {
  id: string;
  label: string;
  shortLabel: string;
  layerKey?: string;
  year?: number;
};

type CandidateLayer = {
  year: number;
  layerKey: string;
  layer: FeatureLayer;
  resolutionScore: number;
};

function parseGeodanmarkYear(label: string) {
  const match = label.match(/geodanmark\s+(\d{4})/i);
  return match ? Number(match[1]) : null;
}

function isTwelvePointFiveCentimeter(label: string) {
  return /12\s*5cm/i.test(label);
}

function isSliderYearLayer(featureLayer?: FeatureLayer) {
  const label = featureLayer?.layer?.label;
  if (!label) {
    return false;
  }

  const year = parseGeodanmarkYear(label);

  return Boolean(
    year
    && year >= 2015
    && isHistoricalOrthophotoLayer(featureLayer)
    && isTwelvePointFiveCentimeter(label)
  );
}

export function isHistoricalOrthophotoLayer(featureLayer?: FeatureLayer) {
  return featureLayer?.layer.group === HISTORICAL_ORTHOPHOTO_GROUP;
}

export function isHistoricalOrthophotoSliderLayer(featureLayer?: FeatureLayer) {
  return featureLayer ? isSliderYearLayer(featureLayer) : false;
}

/** True for WMS tile URLs belonging to geodanmark 12.5cm layers used by the historic ortophoto slider. */
export function isGeodanmarkSliderWmsTileUrl(tileUrl: string) {
  try {
    const url = new URL(tileUrl);
    const layers = url.searchParams.get("LAYERS") ?? url.searchParams.get("layers");
    return Boolean(
      layers?.match(/^geodanmark_\d{4}_12_5cm$/i)
      && url.hostname.includes("dataforsyningen.dk")
    );
  } catch {
    return /geodanmark_\d{4}_12_5cm/i.test(tileUrl) && tileUrl.includes("dataforsyningen.dk");
  }
}

export function getHistoricalOrthophotoOptions(featureLayers: Record<string, FeatureLayer>) {
  const bestByYear = new Map<number, CandidateLayer>();

  Object.entries(featureLayers).forEach(([layerKey, featureLayer]) => {
    if (!isSliderYearLayer(featureLayer)) {
      return;
    }

    const year = parseGeodanmarkYear(featureLayer.layer.label);
    if (!year) {
      return;
    }

    const candidate: CandidateLayer = {
      year,
      layerKey,
      layer: featureLayer,
      resolutionScore: 1,
    };

    const existing = bestByYear.get(year);
    if (!existing || candidate.resolutionScore > existing.resolutionScore) {
      bestByYear.set(year, candidate);
    }
  });

  const yearOptions: HistoricalOrthophotoOption[] = [...bestByYear.values()]
    .sort((a, b) => a.year - b.year)
    .map((candidate) => ({
      id: candidate.layerKey,
      layerKey: candidate.layerKey,
      year: candidate.year,
      label: String(candidate.year),
      shortLabel: String(candidate.year),
    }));

  return [
    ...yearOptions,
    {
      id: HISTORICAL_ORTHOPHOTO_CURRENT_ID,
      label: HISTORICAL_ORTHOPHOTO_CURRENT_LABEL,
      shortLabel: HISTORICAL_ORTHOPHOTO_CURRENT_LABEL,
    },
  ];
}

export function getHistoricalOrthophotoLayerKeys(featureLayers: Record<string, FeatureLayer>) {
  return getHistoricalOrthophotoOptions(featureLayers)
    .map((option) => option.layerKey)
    .filter((layerKey): layerKey is string => Boolean(layerKey));
}
